import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { useSnackbar } from 'notistack';

import patchWarehouse from '../../thunks/coilovers/warehouse/patchWarehouse';
import postWarehouse from '../../thunks/coilovers/warehouse/postWarehouse';
import { clearPatchWarehouseParts, clearPostWarehouseParts } from '../../actions/coilovers/warehouse/patchWarehouse';
import getOrderedPartTypeList from '../../thunks/general/getOrderedPartTypeList';
import Modal from '../Modal/Modal';
import { clearPatchOrderedParts } from '../../actions/coilovers/orderedParts/patchOrderedParts';
import getBrandList from '../../thunks/general/getBrandList';
import CustomSelect from '../Select/CustomSelect';
import Text from '../Text/Text';
import setCreatePartTypeModalState from '../../actions/createPartTypeModal/createPartTypeModal';
import { warehousePartsNeededList, warehouseStatusList } from '../../constants/warehousePartsStatus';
import Input from '../Input/Input';
import ConfirmModal from '../ConfirmModal/ConfirmModal';
import AlertType from '../../common/alertType';
import Alert from '../Alert/Alert';
import { orderedPartsIncludeList } from '../../constants/orderedPartsInclude';

const WarehouseEditModal = ({ visible, toggleVisible, item }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const dispatch = useDispatch();
  const {
    patchWarehouse: {
      loading, error: patchError, data: patchedData
    },
    postWarehouse: {
      loading: postLoading, error: postError, data: postedData
    },
    brandList,
    brandListLoading,
    getOrderedPartsList,
    getOrderedPartsListLoading
  } = useSelector((state) => ({
    patchWarehouse: state?.patchWarehouse,
    postWarehouse: state?.postWarehouse,
    brandList: state?.getBrandList.data,
    brandListLoading: state?.getBrandList.loading,
    getOrderedPartsList: state?.getOrderedPartsList?.data,
    getOrderedPartsListLoading: state?.getOrderedPartsList?.loading,
  }), shallowEqual);

  const {
    _id,
    part_whs_id: whsId,
    part_whs_available_qty: availableQty,
    part_whs_average_monthly_used: monthlyUsed,
    part_whs_brand: brand,
    part_whs_for_coilover: forCoilover,
    part_whs_location: location,
    part_whs_mpn: mpn,
    part_whs_need_for_order_qty: needForOrder,
    part_whs_ordered_needed_status: orderedNeededStatus,
    part_whs_ordered_qty: orderedQty,
    part_whs_reserved_qty: reservedQty,
    part_whs_status: status,
    part_whs_to_order: toOrder,
    part_whs_total_qty: totalQty,
    part_whs_type: type,
    part_whs_note: note,
    part_whs_min_qty: minQty,
    part_whs_max_qty: maxQty,
    part_whs_total_used: totalUsedQty,
    part_whs_return_qty: returnQty,
    part_whs_price: price,
    part_whs_included_type: includedType,
    part_whs_shipping_length: shippingLength,
    part_whs_shipping_width: shippingWidth,
    part_whs_shipping_height: shippingHeight,
    part_whs_shipping_weight: shippingWeight,
  } = item;

  useEffect(() => {
    if (Object.keys(patchedData).length || Object.keys(postedData).length) {
      enqueueSnackbar(`Part ${whsId ? 'updated' : 'created'} successfully`, { variant: 'success' });
      batch(() => {
        dispatch(clearPatchWarehouseParts());
        dispatch(clearPostWarehouseParts());
      });
      toggleVisible();
    }
  }, [patchedData, postedData]);

  const {
    values: {
      partAvailableQty,
      partMonthlyUsed,
      partBrand,
      partForCoilover,
      partLocation,
      partMpn,
      partNeedForOrder,
      partOrderedNeededStatus,
      partOrderedQty,
      partReservedQty,
      partStatus,
      partToOrder,
      partTotalQty,
      partType,
      partNote,
      partMinQty,
      partMaxQty,
      partTotalUsedQty,
      partReturnQty,
      partPrice,
      partIncludedType,
      partShippingLength,
      partShippingWidth,
      partShippingHeight,
      partShippingWeight,
    },
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    isValid,
    errors,
    touched
  } = useFormik({
    initialValues: {
      partAvailableQty: 0,
      partMonthlyUsed: 0,
      partNeedForOrder: 0,
      partOrderedQty: 0,
      partReservedQty: 0,
      partToOrder: 0,
      partTotalQty: 0,
      partMinQty: 0,
      partMaxQty: 0,
      partTotalUsedQty: 0,
      partReturnQty: 0,
      partPrice: 0,
      partShippingLength: 0,
      partShippingWidth: 0,
      partShippingHeight: 0,
      partShippingWeight: 0,
      partBrand: null,
      partOrderedNeededStatus: null,
      partStatus: null,
      partType: null,
      partForCoilover: '',
      partLocation: '',
      partMpn: '',
      partNote: '',
      partIncludedType: null,
    },
    onSubmit: (values) => {
      const action = whsId ? patchWarehouse : postWarehouse;
      dispatch(action({
        _id,
        formData: {
          part_whs_available_qty: parseInt(values?.partAvailableQty),
          part_whs_average_monthly_used: parseInt(values?.partMonthlyUsed),
          part_whs_brand: values?.partBrand?.value,
          part_whs_for_coilover: values?.partForCoilover,
          part_whs_location: values?.partLocation,
          part_whs_mpn: values?.partMpn,
          part_whs_need_for_order_qty: parseInt(values?.partNeedForOrder),
          part_whs_ordered_needed_status: values?.partOrderedNeededStatus?.value,
          part_whs_ordered_qty: parseInt(values?.partOrderedQty),
          part_whs_reserved_qty: parseInt(values?.partReservedQty),
          part_whs_status: values?.partStatus?.value,
          part_whs_to_order: parseInt(values?.partToOrder),
          part_whs_total_qty: parseInt(values?.partTotalQty),
          part_whs_type: values?.partType?.value,
          part_whs_note: values?.partNote,
          part_whs_min_qty: parseInt(values?.partMinQty),
          part_whs_max_qty: parseInt(values?.partMaxQty),
          part_whs_total_used: parseInt(values?.partTotalUsedQty),
          part_whs_return_qty: parseInt(values?.partReturnQty),
          part_whs_price: parseFloat(values?.partPrice),
          part_whs_included_type: values?.partIncludedType?.value,
          part_whs_shipping_length: parseFloat(values?.partShippingLength),
          part_whs_shipping_width: parseFloat(values?.partShippingWidth),
          part_whs_shipping_height: parseFloat(values?.partShippingHeight),
          part_whs_shipping_weight: parseFloat(values?.partShippingWeight),
        }
      }));
    },
  });

  useEffect(() => {
    setValues({
      partAvailableQty: availableQty || 0,
      partMonthlyUsed: monthlyUsed || 0,
      partNeedForOrder: needForOrder || 0,
      partOrderedQty: orderedQty || 0,
      partReservedQty: reservedQty || 0,
      partToOrder: toOrder || 0,
      partTotalQty: totalQty || 0,
      ...(brand && ({ partBrand: { label: brand, value: brand } })),
      ...(orderedNeededStatus
          && ({ partOrderedNeededStatus: { label: orderedNeededStatus, value: orderedNeededStatus } })),
      ...(status && ({ partStatus: { label: status, value: status } })),
      ...(type && ({ partType: { label: type, value: type } })),
      partForCoilover: forCoilover || '',
      partLocation: location || '',
      partMpn: mpn || '',
      partNote: note || '',
      partMinQty: minQty || 0,
      partMaxQty: maxQty || 0,
      partTotalUsedQty: totalUsedQty || 0,
      partReturnQty: returnQty || 0,
      partPrice: price || 0,
      partShippingLength: shippingLength || 0,
      partShippingWidth: shippingWidth || 0,
      partShippingHeight: shippingHeight || 0,
      partShippingWeight: shippingWeight || 0,
      ...(includedType && ({ partIncludedType: { label: includedType, value: includedType } })),
    }, true);
  }, [item]);

  useEffect(() => {
    if (visible && !brandList.length && !brandListLoading) {
      dispatch(getBrandList());
    }
  }, [visible, brandList.length, brandListLoading, dispatch]);

  useEffect(() => {
    if (visible && !getOrderedPartsList.length && !getOrderedPartsListLoading) {
      dispatch(getOrderedPartTypeList());
    }
  }, [visible, getOrderedPartsList.length, getOrderedPartsListLoading, dispatch]);

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={() => {
          toggleVisible();
          dispatch(clearPatchOrderedParts());
        }}
        title={`${whsId ? 'Edit' : 'New'} part`}
        subTitle={whsId && `ID: ${whsId}`}
        saveText={`${whsId ? 'Update' : 'Create'} part`}
        onSave={() => setConfirmVisible(true)}
        canSave={!isValid || loading || postLoading}
      >
        <div className="customScroll w-[500px] px-[10px]">
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Part status:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <CustomSelect
                value={[partStatus]}
                options={warehouseStatusList}
                onChange={(data) => setFieldValue('partStatus', { ...data })}
                placeholder="Select status"
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Part type:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <CustomSelect
                value={[partType]}
                options={getOrderedPartsList}
                onChange={(data) => setFieldValue('partType', { ...data })}
                placeholder={`${getOrderedPartsListLoading ? 'Loading...' : 'Select type'}`}
                isDisabled={getOrderedPartsListLoading}
                infoText="Create type"
                onInfoClick={() => dispatch(setCreatePartTypeModalState({ visible: true }))}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Needed status:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <CustomSelect
                value={[partOrderedNeededStatus]}
                options={warehousePartsNeededList}
                onChange={(data) => setFieldValue('partOrderedNeededStatus', { ...data })}
                placeholder="Select status"
              />
            </div>
          </div>
          <br />
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Part brand:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <CustomSelect
                value={[partBrand]}
                options={brandList}
                placeholder={brandListLoading ? 'Loading...' : 'Select brand'}
                isDisabled={brandListLoading}
                isSearchable
                onChange={(data) => setFieldValue('partBrand', { ...data })}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Part MPN:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="text"
                name="partMpn"
                value={partMpn || ''}
                placeholder="Part mpn"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partMpn && errors?.partMpn}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Included:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <CustomSelect
                value={[partIncludedType]}
                options={orderedPartsIncludeList}
                placeholder="Select included"
                isSearchable
                onChange={(data) => setFieldValue('partIncludedType', { ...data })}
              />
            </div>
          </div>
          <br />
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="For coilovers:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="text"
                name="partForCoilover"
                value={partForCoilover || ''}
                placeholder="For coilovers"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partForCoilover && errors?.partForCoilover}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Location:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="text"
                name="partLocation"
                value={partLocation || ''}
                placeholder="Location"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partLocation && errors?.partLocation}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Total qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partTotalQty"
                value={`${partTotalQty}`}
                placeholder="Total qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partTotalQty && errors?.partTotalQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Available qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partAvailableQty"
                value={`${partAvailableQty}`}
                placeholder="Available qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partAvailableQty && errors?.partAvailableQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Reserved qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partReservedQty"
                value={`${partReservedQty}`}
                placeholder="Reserved qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partReservedQty && errors?.partReservedQty}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Need qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partNeedForOrder"
                value={`${partNeedForOrder}`}
                placeholder="Need for order qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partNeedForOrder && errors?.partNeedForOrder}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Ordered qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partOrderedQty"
                value={`${partOrderedQty}`}
                placeholder="Ordered qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partOrderedQty && errors?.partOrderedQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Monthly used:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partMonthlyUsed"
                value={`${partMonthlyUsed}`}
                placeholder="Monthly used"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partMonthlyUsed && errors?.partMonthlyUsed}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="To order qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partToOrder"
                value={`${partToOrder}`}
                placeholder="To order qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partToOrder && errors?.partToOrder}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Min qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partMinQty"
                value={`${partMinQty}`}
                placeholder="Min qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partMinQty && errors?.partMinQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Max qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partMaxQty"
                value={`${partMaxQty}`}
                placeholder="Max qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partMaxQty && errors?.partMaxQty}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Total used:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partTotalUsedQty"
                value={`${partTotalUsedQty}`}
                placeholder="Total used qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partTotalUsedQty && errors?.partTotalUsedQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Return qty:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partReturnQty"
                value={`${partReturnQty}`}
                placeholder="Return qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partReturnQty && errors?.partReturnQty}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Price:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partPrice"
                step={0.01}
                value={`${partPrice}`}
                placeholder="Price"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partPrice && errors?.partPrice}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Shipping length:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partShippingLength"
                step={0.01}
                value={`${partShippingLength}`}
                placeholder="Shipping length"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partShippingLength && errors?.partShippingLength}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Shipping width:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partShippingWidth"
                step={0.01}
                value={`${partShippingWidth}`}
                placeholder="Shipping width"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partShippingWidth && errors?.partShippingWidth}
              />
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex1">
              <Text text="Shipping height:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partShippingHeight"
                step={0.01}
                value={`${partShippingHeight}`}
                placeholder="Shipping height"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partShippingHeight && errors?.partShippingHeight}
              />
            </div>
            <span className="mr-[20px]" />
            <div className="flex1">
              <Text text="Shipping weight:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
              <Input
                containerStyle="d-col"
                inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
                type="number"
                name="partShippingWeight"
                step={0.01}
                value={`${partShippingWeight}`}
                placeholder="Shipping weight"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={touched?.partShippingWeight && errors?.partShippingWeight}
              />
            </div>
          </div>
          <div className="flex1">
            <Text text="Note:" styleContainer="mb-[6px]" textStyle="text-[13px]" />
            <Input
              multiline
              containerStyle="d-col"
              inputStyle="py-[11px] px-[13px] font-normal text-[13px]"
              type="text"
              name="partNote"
              value={partNote || ''}
              placeholder="Note"
              onBlur={handleBlur}
              onChange={handleChange}
              errorText={touched?.partNote && errors?.partNote}
            />
          </div>
          {(patchError || postError) && <Alert text={patchError || postError} type={AlertType.warning} />}
        </div>
      </Modal>
      <ConfirmModal
        onConfirm={() => {
          setConfirmVisible(false);
          handleSubmit();
        }}
        visible={confirmVisible}
        toggleVisible={() => {
          setConfirmVisible(false);
          toggleVisible();
        }}
      />
    </>
  );
};

WarehouseEditModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  item: PropTypes.shape({
    _id: PropTypes.string,
    part_whs_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_available_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_average_monthly_used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_brand: PropTypes.string,
    part_whs_for_coilover: PropTypes.string,
    part_whs_location: PropTypes.string,
    part_whs_mpn: PropTypes.string,
    part_whs_need_for_order_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_ordered_needed_status: PropTypes.string,
    part_whs_ordered_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_reserved_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_status: PropTypes.string,
    part_whs_to_order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_total_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_type: PropTypes.string,
    part_whs_note: PropTypes.string,
    part_whs_min_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_max_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_total_used: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_return_qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_included_type: PropTypes.string,
    part_whs_shipping_length: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_shipping_width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_shipping_height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    part_whs_shipping_weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
};

WarehouseEditModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
  item: {}
};

export default WarehouseEditModal;
